import {
  isBackendReachable,
  subscribeBackendReachability,
} from './backendReachability';
import { isActuallyOnline } from './checkConnectivity';
import { subscribeToNativeNetwork } from './nativeNetwork';

/**
 * Latido de alcanzabilidad — SOLO para salir del modo caché.
 *
 * La app pasa a modo caché cuando una petición vence su techo, y sale con la
 * primera respuesta real que vuelva. Hasta 2026-09-28 nada estaba diseñado
 * para provocar esa respuesta: lo hacía, por casualidad, el sondeo de 30 s del
 * cronómetro (Apollo manda los sondeos a la red aunque `prioritizeCacheValues`
 * esté activo). Si ese sondeo cambiaba, la app podía quedarse en modo caché con
 * la cobertura ya de vuelta — y en ese modo la cola simple NO sale a la red,
 * así que los tramos no subirían.
 *
 * Solo pregunta mientras el veredicto es `false`, y no lo toca: lo apunta
 * `isActuallyOnline` con lo que conteste el servidor. No ENTRA en modo caché a
 * propósito: el primer sondeo tras despertar la tablet puede pasar de 2,5 s solo
 * por rehacer la conexión TLS, y eso sería un falso «sin servidor».
 *
 * Spec: docs/superpowers/specs/2026-09-28-cola-compound-y-latido-design.md § 3
 */
export const HEARTBEAT_INTERVAL_MS = 30_000;

/**
 * Sondeos de salida pronto, tras abrir el cortacircuitos (spec 2026-10-09 § 9).
 * Si fue un falso «sin servidor» (red que se asentaba al despertar), el aviso
 * que lo habría sacado —primer plano, red nativa— ya pasó antes de abrir; sin
 * esto quedaba pegado hasta el tic de 30 s. Solo los pide el cortacircuitos:
 * un `false` del techo de 12 s (red colgada) no se vuelve a probar tan pronto.
 */
export const EARLY_PROBE_DELAYS_MS: readonly number[] = [3_000, 10_000];

let started = false;
let inFlight = false;
let timer: ReturnType<typeof setInterval> | null = null;
let cleanup: (() => void) | null = null;
let earlyTimers: Array<ReturnType<typeof setTimeout>> = [];

const enModoSinServidor = (): boolean => isBackendReachable() === false;

async function probe(): Promise<void> {
  if (inFlight || !enModoSinServidor()) return;
  inFlight = true;
  try {
    await isActuallyOnline();
  } catch {
    // Un sondeo que revienta no dice nada nuevo; el siguiente lo intentará.
  } finally {
    inFlight = false;
  }
}

function syncTimer(): void {
  if (enModoSinServidor() && !timer) {
    timer = setInterval(() => void probe(), HEARTBEAT_INTERVAL_MS);
  } else if (!enModoSinServidor() && timer) {
    clearInterval(timer);
    timer = null;
  }
}

/** Programa los sondeos de salida tempranos (los pide el cortacircuitos al abrir). */
export function probeReachabilitySoon(): void {
  earlyTimers.forEach(clearTimeout);
  earlyTimers = EARLY_PROBE_DELAYS_MS.map((delay) => setTimeout(() => void probe(), delay));
}

/** Arranca el latido una sola vez (StrictMode ejecuta los efectos dos veces). */
export function startReachabilityHeartbeat(): () => void {
  if (started && cleanup) return cleanup;
  started = true;

  const onVisible = (): void => {
    if (document.visibilityState === 'visible') void probe();
  };
  // Web: vuelve la red del navegador. Sin esto, si el modo entró con el aparato
  // perdiendo la red, la salida esperaba al tic de 30 s (el APK ya lo cubre con
  // el aviso nativo de abajo).
  const onOnline = (): void => void probe();
  const unsubReachability = subscribeBackendReachability(syncTimer);
  const unsubNative = subscribeToNativeNetwork((connected) => {
    if (connected) void probe();
  });
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('online', onOnline);
  syncTimer();

  cleanup = () => {
    unsubReachability();
    unsubNative();
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('online', onOnline);
    if (timer) clearInterval(timer);
    timer = null;
    started = false;
    cleanup = null;
  };
  return cleanup;
}

/** Solo para tests. */
export function _resetReachabilityHeartbeatForTests(): void {
  cleanup?.();
  earlyTimers.forEach(clearTimeout);
  earlyTimers = [];
  inFlight = false;
}
